import { FC, PropsWithChildren, createContext, useContext, useMemo } from 'react';
import { StoreApi, createStore, useStore } from 'zustand';
import { handleMutation } from '../../helpers/toastHelper';
import { useFlag } from '../backstage/BackstageProvider';
import {
    useGetOrganisationPropertiesQuery,
    useGetOrganisationSettingsQuery,
    useSetOrganisationPropertiesMutation
} from '../gql/graphql.generated';
import { OptimiserEnabledSolvers, SystemSettings, defaultSettings } from './systemSettings.types';
import { useGetBreakOffsets } from './useGetBreakOffsets';

const ESS_BEHAVIOUR_KEYS = {
  allowSelfEditEmail: 'essBehaviour.allowSelfEditEmail',
  allowSelfEditPhoneNumber: 'essBehaviour.allowSelfEditPhoneNumber'
} as const;

const OPTIMISER_SOLVER_KEYS = {
  enabledSolvers: 'optimiser.enabledSolvers',
  enableGeneticAlgorithm: 'optimiser.enableGeneticAlgorithm',
  enableCpSat: 'optimiser.enableCpSat',
  showEvaluation: 'optimiser.showEvaluation'
} as const;

const getOptimiserEnabledSolvers = (properties: Record<string, unknown>): OptimiserEnabledSolvers => {
  const enabledSolvers = properties[OPTIMISER_SOLVER_KEYS.enabledSolvers];

  if (enabledSolvers === 'geneticAlgorithm' || enabledSolvers === 'cpSat' || enabledSolvers === 'both') {
    return enabledSolvers;
  }

  const enableGeneticAlgorithm = properties[OPTIMISER_SOLVER_KEYS.enableGeneticAlgorithm];
  const enableCpSat = properties[OPTIMISER_SOLVER_KEYS.enableCpSat];

  if (Boolean(enableGeneticAlgorithm) && Boolean(enableCpSat)) {
    return 'both';
  }

  if (Boolean(enableCpSat)) {
    return 'cpSat';
  }

  return defaultSettings.optimiserEnabledSolvers;
};

interface SystemSettingsStore {
  settings: SystemSettings;
  setSettings: (newSettings: SystemSettings) => void;
}

export const SystemSettingsContext = createContext<StoreApi<SystemSettingsStore> | null>(null);

export const SystemSettingsProvider: FC<PropsWithChildren> = ({ children }) => {
  const [{ data }] = useGetOrganisationPropertiesQuery();
  const [{ data: organisationSettingsData }] = useGetOrganisationSettingsQuery();
  const offsetData = useGetBreakOffsets();

  const allowMultipleAvailabilities = useFlag('allowMultipleAvailableTimes');

  const store = useMemo(() => {
    let settings: SystemSettings;

    if (!data?.getOrganisationProperties) {
      settings = { ...defaultSettings };
    } else {
      const properties = JSON.parse(data.getOrganisationProperties);
      if (properties.maxCustomTemplates !== undefined) {
        properties.maxCustomTemplates = parseInt(properties.maxCustomTemplates);
      }

      if (properties.splitShifts?.minSecondsBetween !== undefined) {
        properties.splitShifts.minSecondsBetween = parseInt(properties.splitShifts.minSecondsBetween);
      }

      if (properties.splitShifts?.maxNumber !== undefined) {
        properties.splitShifts.maxNumber = parseInt(properties.splitShifts.maxNumber);
      }

      if (properties.minTime !== undefined) {
        properties.minTime = parseInt(properties.minTime);
      }

      if (properties.maxTime !== undefined) {
        properties.maxTime = parseInt(properties.maxTime);
      }

      if (properties.historicalData !== undefined) {
        properties.historicalData = parseInt(properties.historicalData);
      }

      settings = {
        ...defaultSettings,
        ...properties,
        allowEssEditEmail: Boolean(properties[ESS_BEHAVIOUR_KEYS.allowSelfEditEmail]),
        allowEssEditPhoneNumber: Boolean(properties[ESS_BEHAVIOUR_KEYS.allowSelfEditPhoneNumber]),
        optimiserEnabledSolvers: getOptimiserEnabledSolvers(properties),
        optimiserShowEvaluation: Boolean(
          properties[OPTIMISER_SOLVER_KEYS.showEvaluation] ?? defaultSettings.optimiserShowEvaluation
        )
      };
    }

    settings.holidayRegion = organisationSettingsData?.organisationSettings?.defaultHolidayRegionCode ?? '';

    if (offsetData) {
      settings.timeOffsetAfterLastBreak = offsetData.timeOffsetAfterLastBreak;
      settings.timeOffsetBeforeFirstBreak = offsetData.timeOffsetBeforeFirstBreak;
    }

    if (!allowMultipleAvailabilities) {
      settings.maxTimePeriods = 1;
    }

    return createStore<SystemSettingsStore>()(set => ({
      settings,
      setSettings: settings => set({ settings })
    }));
  }, [allowMultipleAvailabilities, data, offsetData, organisationSettingsData]);

  return <SystemSettingsContext.Provider value={store}>{children}</SystemSettingsContext.Provider>;
};

type SelectorReturn<S extends (s: SystemSettingsStore) => any> = ReturnType<S>;
export function useSystemSettings<S extends (s: SystemSettingsStore) => any>(selector: S): SelectorReturn<S> {
  const context = useContext(SystemSettingsContext);
  if (!context) {
    throw new Error('useSystemSettings must be used within a SystemSettingsProvider');
  }
  return useStore(context, selector);
}

export const useUpdateSystemSettings = () => {
  const [{ data }, refetch] = useGetOrganisationPropertiesQuery();
  const [{ fetching }, updateOrganisationProperties] = useSetOrganisationPropertiesMutation();

  const updateSettings = async (partial: Partial<SystemSettings>) => {
    const existing = JSON.parse(data?.getOrganisationProperties || '{}');
    return handleMutation(
      () =>
        updateOrganisationProperties({
          properties: JSON.stringify({ ...existing, ...partial })
        }),
      {
        successMessage: 'Settings saved successfully.',
        errorMessage: 'Failed to save settings.',
        onSuccess: () => refetch({ requestPolicy: 'network-only' })
      }
    );
  };

  return { updateSettings, fetching };
};
